import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Grid } from '@radix-ui/themes';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { CommonTableButton } from 'components/common/table/button';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  DEFAULT_TOOLBAR_FILTERS,
  IToolbarFilters,
} from 'interfaces/pitch-lists/i-toolbar-filters';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import {
  PITCH_LIST_TYPES,
  PitchType,
  ReferenceListType,
  TRAINING_STATUS_OPTIONS,
  TrainingStatus,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { clone } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';

export const PitchListsToolbar = () => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const lists = useListLibraryStore((s) => s.lists);
  const toolbarFilters = useListLibraryStore((s) => s.toolbarFilters);

  const teamOptions = useMemo<IOptionExt[]>(() => {
    const teamNames = lists.map((l) => l.team?.trim()).filter((t) => t);
    const uniqueTeams = ArrayHelper.unique(teamNames);
    return uniqueTeams.map((t) => ({ label: t, value: t }) as IOptionExt);
  }, [lists]);

  const getGridColCt = () => {
    let count = 5;
    if (teamOptions.length > 0) {
      count += 1;
    }
    if (userRoleFeatureFlags.isSuperAdmin) {
      count += 1;
    }

    return count.toString();
  };

  const mergeFilters = useCallback((filters: Partial<IToolbarFilters>) => {
    useListLibraryStore.setState((prev) => ({
      toolbarFilters: {
        ...prev.toolbarFilters,
        ...filters,
      },
    }));
  }, []);

  // if the team filter is set to a value that is no longer valid (e.g. all lists for that team were deleted), unset that filter value
  useEffect(() => {
    const validTeamFilter = toolbarFilters.teams.filter((id) =>
      teamOptions.find((o) => o.value === id)
    );

    if (validTeamFilter.length !== toolbarFilters.teams.length) {
      mergeFilters({
        teams: validTeamFilter,
      });
    }
  }, [teamOptions, toolbarFilters.teams]);

  return (
    <Grid columns={getGridColCt()} gap={RADIX.FLEX.GAP.SM}>
      <Box>
        <CommonTextInput
          id="pitch-list-search-name"
          name="name"
          placeholder="common.pitch-list-name"
          value={toolbarFilters.name}
          debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
          onChange={(v) => mergeFilters({ name: v })}
          rightSlot={
            toolbarFilters.name ? (
              <Cross2Icon
                className="cursor-pointer"
                onClick={() => mergeFilters({ name: '' })}
              />
            ) : (
              <MagnifyingGlassIcon />
            )
          }
        />
      </Box>
      <Box>
        <CommonSearchInput
          key={`status-${toolbarFilters.key}`}
          id="pitch-lists-status"
          placeholder="pl.status"
          options={TRAINING_STATUS_OPTIONS}
          values={toolbarFilters.training_status}
          onChange={(v) => {
            mergeFilters({
              training_status: v as TrainingStatus[],
            });
          }}
          multiple
        />
      </Box>
      <Box>
        <CommonSearchInput
          key={`pitch-type-${toolbarFilters.key}`}
          id="pitch-lists-type"
          name="filter-type"
          placeholder="pl.pitch-type"
          options={PITCH_TYPE_FILTER_OPTIONS}
          values={toolbarFilters.pitch_type}
          onChange={(v) =>
            mergeFilters({
              pitch_type: v as PitchType[],
            })
          }
          multiple
          skipSort
        />
      </Box>
      {teamOptions.length > 0 && (
        <Box>
          <CommonSearchInput
            key={`pl-team-${toolbarFilters.key}`}
            id="pitch-lists-team"
            name="filter-team"
            placeholder="common.team"
            options={teamOptions}
            values={toolbarFilters.teams}
            onChange={(v) =>
              mergeFilters({
                teams: v as string[],
              })
            }
            multiple
          />
        </Box>
      )}
      <Box>
        <CommonSelectInput
          key={`vis-${toolbarFilters.key}`}
          id="pitch-lists-visibility"
          name="filter-visibility"
          placeholder="common.visibility"
          options={[
            {
              label: 'common.personal',
              value: PitchListOwner.User,
            },
            {
              label: 'common.machine',
              value: PitchListOwner.Machine,
            },
            {
              label: 'common.team',
              value: PitchListOwner.Team,
            },
          ]}
          value={toolbarFilters.visibility}
          onChange={(v) =>
            mergeFilters({
              visibility: v as PitchListOwner,
            })
          }
          optional
        />
      </Box>
      {userRoleFeatureFlags.isSuperAdmin && (
        <Box>
          <CommonSelectInput
            key={`type-${toolbarFilters.key}`}
            id="pitch-lists-type"
            name="filter-type"
            placeholder="pl.list-type"
            inputColor={RADIX.COLOR.SUPER_ADMIN}
            options={PITCH_LIST_TYPES}
            value={toolbarFilters.reference_type}
            onChange={(v) =>
              mergeFilters({
                reference_type: v as ReferenceListType,
              })
            }
            optional
          />
        </Box>
      )}
      <Box>
        <CommonTableButton
          label="hitters.reset-filters"
          className="btn-block"
          color={RADIX.COLOR.NEUTRAL}
          variant="soft"
          onClick={() =>
            useListLibraryStore.setState({
              toolbarFilters: clone(DEFAULT_TOOLBAR_FILTERS),
            })
          }
        />
      </Box>
    </Grid>
  );
};
